import { Injectable } from "@angular/core";
import { HttpService } from "app/js/src/infrastructure/http/http";
import { IClippingStudioGraphicsRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-graphics-repository";
import { ClippingStudioGraphicCatalogItem } from "app/js/src/domain/clipping-studio/clipping-studio-graphic-catalog";
import type { GraphicType } from "app/js/src/domain/clipping-studio/clipping-studio-project";

/** API response item (PascalCase from backend). */
interface GraphicsCatalogApiItem {
  Id: string;
  TenantId?: number;
  Type: string;
  Name: string;
  MainText: string;
  SubText: string;
  Color: string;
  ThumbnailUrl?: string;
  DurationLabel: string;
  LogoKey?: string;
}

const VALID_GRAPHIC_TYPES: GraphicType[] = [
  "logo",
  "fruit-icon",
  "emoji",
];

function toGraphicType(s: string): GraphicType {
  const lower = (s || "").toLowerCase();
  return VALID_GRAPHIC_TYPES.includes(lower as GraphicType)
    ? (lower as GraphicType)
    : "logo";
}

@Injectable()
export class ClippingStudioGraphicsHttpRepositoryService extends IClippingStudioGraphicsRepository {
  private readonly baseUrl = "clipping-studio/graphics";

  constructor(private readonly httpService: HttpService) {
    super();
  }

  async getCatalogItems(): Promise<ClippingStudioGraphicCatalogItem[]> {
    const raw = await this.httpService.get(`${this.baseUrl}/catalog`);
    return this.mapItems(raw);
  }

  async searchCatalogItems(query: string): Promise<ClippingStudioGraphicCatalogItem[]> {
    const encoded = encodeURIComponent(query);
    const raw = await this.httpService.get(
      `${this.baseUrl}/catalog/search?q=${encoded}`,
    );
    return this.mapItems(raw);
  }

  private mapItems(raw: unknown): ClippingStudioGraphicCatalogItem[] {
    const arr = Array.isArray(raw) ? raw : [];
    return (arr as GraphicsCatalogApiItem[]).map((item) => this.mapItem(item));
  }

  private mapItem(item: GraphicsCatalogApiItem): ClippingStudioGraphicCatalogItem {
    return {
      id: item.Id,
      type: toGraphicType(item.Type),
      name: item.Name ?? "",
      mainText: item.MainText ?? "",
      subText: item.SubText ?? "",
      color: item.Color ?? "",
      thumbnailUrl: item.ThumbnailUrl ?? undefined,
      durationLabel: item.DurationLabel ?? "",
      logoKey: item.LogoKey ?? undefined,
    };
  }
}
